import { useMemo, useState } from "react";

import { Link } from "react-router-dom";

import { ArrowLeft, FolderTree, Pencil, Plus, Search, Trash2 } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useAccountingCategories,
  useCompanies,
  useCreateAccountingCategory,
  useDeleteAccountingCategory,
  useUpdateAccountingCategory,
} from "~/hooks/contabilidad";

import { CategoriaContableDialog } from "./components/CategoriaContableDialog";

import type {
  AccountingCategory,
  AccountingCategoryFormData,
  CostingMethod,
  InventoryValuation,
} from "~/types/contabilidad";


// "Aliace Pruebas" — the fictitious company used for accounting tests (accounts already created there).
const ALIACE_PRUEBAS_ID = "9722c001-a64a-411f-8b68-b99261005089";

const COSTING_LABEL: Record<CostingMethod, string> = {
  average: "Promedio",
  standard: "Estándar",
  fifo: "FIFO",
};
const VALUATION_LABEL: Record<InventoryValuation, string> = {
  perpetual: "Permanente (al facturar)",
  manual: "Periódico (manual)",
};

export default function CategoriasContablesPage() {
  const [companyFilter, setCompanyFilter] = useState<string>(ALIACE_PRUEBAS_ID);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AccountingCategory | undefined>();
  const [deleting, setDeleting] = useState<AccountingCategory | undefined>();

  const { data: companies = [] } = useCompanies();
  const { data: categories = [], isLoading } = useAccountingCategories({
    company_id: companyFilter,
  });

  const createMutation = useCreateAccountingCategory();
  const updateMutation = useUpdateAccountingCategory();
  const deleteMutation = useDeleteAccountingCategory();

  const filtered = useMemo(
    () =>
      search
        ? categories.filter((c) =>
            c.name.toLowerCase().includes(search.toLowerCase())
          )
        : categories,
    [categories, search]
  );

  const handleCreate = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };
  const handleEdit = (cat: AccountingCategory) => {
    setEditing(cat);
    setDialogOpen(true);
  };
  const handleSubmit = (data: AccountingCategoryFormData) => {
    if (editing) {
      updateMutation.mutate(
        { id: editing.id, data },
        { onSuccess: () => setDialogOpen(false) }
      );
    } else {
      createMutation.mutate(data, { onSuccess: () => setDialogOpen(false) });
    }
  };
  const confirmDelete = () => {
    if (deleting) {
      deleteMutation.mutate(deleting.id, {
        onSuccess: () => setDeleting(undefined),
      });
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Categorías Contables"
        description="Valoración y asignación de cuentas por categoría de producto (ingresos, gastos, inventario, variación)."
        back={
          <Link to="/contabilidad/configuracion">
            <Button variant="ghost" size="sm" className="-ml-2 h-9 gap-1">
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
          </Link>
        }
        actions={
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva Categoría
          </Button>
        }
      />

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="h-9 w-full sm:w-64">
                <SelectValue placeholder="Empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar categoría..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-full pl-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={FolderTree}
              title="Sin categorías"
              description="Cree una categoría para asignar sus cuentas contables."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Método de costo</TableHead>
                  <TableHead>Valoración</TableHead>
                  <TableHead>Ingresos</TableHead>
                  <TableHead>Gastos</TableHead>
                  <TableHead>Inventario</TableHead>
                  <TableHead>Variación</TableHead>
                  <TableHead className="w-0 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell className="font-medium">{cat.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {COSTING_LABEL[cat.costing_method] ?? cat.costing_method}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {VALUATION_LABEL[cat.inventory_valuation] ??
                        cat.inventory_valuation}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {cat.income_account?.code ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {cat.expense_account?.code ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {cat.inventory_account?.code ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {cat.inventory_variation_account?.code ?? "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label="Editar"
                          onClick={() => handleEdit(cat)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          aria-label="Eliminar"
                          onClick={() => setDeleting(cat)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CategoriaContableDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category={editing}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        defaultCompanyId={companyFilter}
      />

      <AlertDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar categoría</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar la categoría "{deleting?.name}"? Esta
              acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
